import { useTranslation } from '@wix/fe-essentials/react';
import { RuleListStatus } from '@wix/rules-panel-ui';
import { PagesSmall } from '@wix/wix-ui-icons-common';
import { useSetAtom } from 'jotai';
import React, { useCallback, useMemo } from 'react';
import { verticalsDrillInPageIdAtom } from '../../../../../atoms/session-atoms';
import { usePanelStateAtom } from '../../../../../hooks/rules-state';
import { useNavigation } from '../../../../../hooks/use-navigation';
import { getSubtitles } from '../../../../../rule-mappers/build-rule-props/get-subtitles';
import { getNumberOfIssuesInVerticalGroup } from '../../../../../rule-mappers/shared/helpers';
import { useVerify } from '../../../../../rule-mappers/shared/use-verify';
import { resolveGroupAnalysisState } from '../../../../../utils/resolve-group-analysis-state';
import { VerifyPageButton } from '../../../verify-page-button';
import { useRuleGroupUtils } from '../../rule-groups/rule-group-utils';

// Overload 1: Button variant when isTpaPage is true

// Overload 2: Collapsible variant when isTpaPage is false

// General overload

export function usePageListItemBase(_ref) {
  let {
    pageId,
    isPageOpen,
    isTpaPage,
    ruleResults
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    getGroupData,
    getPagesData,
    panelState
  } = usePanelStateAtom();
  const {
    getIssueCount
  } = useRuleGroupUtils();
  const {
    verify
  } = useVerify({});
  const setVerticalsDrillInPageId = useSetAtom(verticalsDrillInPageIdAtom);
  const navigateToPage = useNavigation();
  const groupData = getGroupData(pageId);
  if (!groupData) {
    throw new Error(`Group data not found for pageId: ${pageId}.`);
  }
  const {
    analysisState,
    title,
    rulesStateMap,
    scannedPages,
    itemType
  } = groupData;
  const {
    isAnalyzeFailed,
    isFetchResultSucceeded,
    isFirstAnalysis,
    isAnalyzing
  } = useMemo(() => resolveGroupAnalysisState(analysisState), [analysisState]);
  const pageIdToPageData = getPagesData();
  const issueCount = useMemo(() => {
    if (isTpaPage) {
      return isFetchResultSucceeded ? getNumberOfIssuesInVerticalGroup(panelState, itemType) : 0;
    }
    if (ruleResults && ruleResults.length > 0) {
      return ruleResults.filter(ruleResult => ruleResult.status === RuleListStatus.failed).length;
    }
    return getIssueCount(rulesStateMap);
  }, [isTpaPage, ruleResults, getIssueCount, rulesStateMap, isFetchResultSucceeded, panelState, itemType]);
  const badgeText = useMemo(() => {
    if (isAnalyzeFailed) {
      return t('accessibility-wizard.rule-group.issue-count-badge.label.scan-failed');
    }
    if (issueCount > 0 && !isAnalyzing) {
      return t('accessibility-wizard.rule-group.issue-count-badge.label', {
        numberOfIssues: issueCount
      });
    }
    return undefined;
  }, [isAnalyzeFailed, issueCount, t, isAnalyzing]);
  const subtitle = useMemo(() => {
    var _pageIdToPageData$get;
    return isTpaPage ? getSubtitles({
      groupData,
      scannedPagesAmount: scannedPages ?? 0,
      t,
      totalPagesToScan: ((_pageIdToPageData$get = pageIdToPageData.get(pageId)) == null || (_pageIdToPageData$get = _pageIdToPageData$get.innerRoutes) == null ? void 0 : _pageIdToPageData$get.length) ?? 0
    }) : undefined;
  }, [groupData, scannedPages, t, pageIdToPageData, pageId, isTpaPage]);
  const onVerifyClick = useCallback(() => {
    const globalComponentIds = panelState.globalSectionComponentsDataMap ? Array.from(panelState.globalSectionComponentsDataMap.values()).map(_ref2 => {
      let {
        componentId
      } = _ref2;
      return componentId;
    }) : [];
    return verify(groupData, globalComponentIds);
  }, [verify, groupData, panelState.globalSectionComponentsDataMap]);
  const suffix = useMemo(() => {
    return isAnalyzeFailed || isPageOpen || isAnalyzing ? /*#__PURE__*/React.createElement(VerifyPageButton, {
      failedTaskCount: issueCount,
      isLoading: isAnalyzing,
      pageId: pageId,
      taskCount: issueCount,
      onClick: onVerifyClick
    }) : null;
  }, [isAnalyzeFailed, isPageOpen, isAnalyzing, issueCount, pageId, onVerifyClick]);
  const baseProps = {
    badgeText,
    badgeTooltipContent: isAnalyzeFailed ? t('accessibility-wizard.rule-group.issue-count-badge.tooltip-content.scan-failed') : undefined,
    disabled: isAnalyzeFailed,
    id: pageId,
    isLoading: isFirstAnalysis,
    issueCount,
    subtitle,
    suffix,
    title
  };
  const prefix = useMemo(() => /*#__PURE__*/React.createElement(PagesSmall, null), []);
  const onButtonClick = useCallback(id => {
    setVerticalsDrillInPageId(id);
    navigateToPage(id);
  }, [setVerticalsDrillInPageId, navigateToPage]);
  if (isTpaPage) {
    return {
      ...baseProps,
      onButtonClick,
      prefix,
      variant: 'button'
    };
  }
  return {
    ...baseProps,
    variant: 'collapsible'
  };
}
//# sourceMappingURL=use-page-list-item-base.js.map